import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import apiService from '../../../services/api';
import { usePageLoaderContext } from '../../../contexts/PageLoaderContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useWorkingMode } from '../../../contexts/WorkingModeContext';
import styles from '../../../styles/TaskDetails.module.css';
import tabStyles from '../../../styles/TabContainer.module.css';
import SkeletonLoader from '../../UI/SkeletonLoader';
import ChatContainer from '../../Chat/Chat';
import TaskUpdates from './TaskUpdates';
import TaskSubmission from './TaskSubmission';
import TaskInstances from './TaskInstances';
import TemplateStatistics from './TemplateStatistics';
import TaskStatistics from './TaskStatistics';
import TaskCharts from './TaskCharts';
import TaskHeader from './TaskHeader';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import TimelineItemCountdown from './TimelineItemCountdown';

interface User {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  username: string;
  email: string;
  full_name?: string;
}

interface TaskCategory {
  id: string;
  name: string;
  color: string;
}

interface TaskAssignment {
  id: string;
  user: User;
  points_earned: number;
  status: 'pending' | 'in_progress' | 'completed';
  assigned_points: number;
  assigned_percentage: number;
  work_description: string;
  is_team_lead: boolean;
}

interface TaskDocument {
  id: string;
  title: string;
  filename: string;
  file_size: number;
  file_size_display: string;
  file_type: string;
  document_type: string;
  description: string;
  is_required: boolean;
  uploaded_by: User;
  uploaded_at: string;
  file_url: string;
}

interface TaskCommentAttachment {
  id: string;
  filename: string;
  file_size: number;
  file_type: string;
  file_size_display: string;
  url: string;
}

interface TaskCommentReaction {
  count: number;
  users: {
    first_name: string;
    last_name: string;
    username: string;
    full_name: string;
  }[];
}

interface TaskComment {
  id: string;
  content: string;
  action_type: string;
  attachments: TaskCommentAttachment[];
  reactions: { [emoji: string]: TaskCommentReaction };
  replies?: TaskComment[];
  parent_comment?: string;
  created_by: User;
  created_at: string;
}

interface GroupInfo {
  id: string;
  name: string;
  description: string;
}

interface DeadlineRule {
  id: string;
  name: string;
  description: string;
  time_period_type: 'before' | 'after' | 'range' | 'exact';
  calculation_type: 'fixed' | 'percentage' | 'percentage_total' | 'multiplier';
  point_value: number;
  time_before?: { days: number; hours: number; minutes: number; seconds: number };
  time_after?: { days: number; hours: number; minutes: number; seconds: number };
  time_range?: { from: { days: number; hours: number; minutes: number; seconds: number }; to: { days: number; hours: number; minutes: number; seconds: number } };
}

interface Approver {
  id: string;
  user: User;
}

interface TaskDetailsData {
  id: string;
  name: string;
  description: string;
  status: string;
  priority: string;
  assignees: TaskAssignment[];
  approvers?: Approver[];
  approver_ids?: string[];
  documents: TaskDocument[];
  comments: TaskComment[];
  created_by: User;
  created_at: string;
  due_date: string | null;
  base_points: number;
  team_lead_points: number;
  deadline_rules: DeadlineRule[];
  category: TaskCategory;
  requires_approval: boolean;
  approval_type?: string;
  requires_documents: boolean;
  enable_group_chat: boolean;
  estimated_hours: number;
  group: GroupInfo | null;
  allow_user_instances?: boolean;
  is_instance?: boolean;
  from_template_id?: string;
  instance_template_name?: string;
  is_user_instance?: boolean;
  is_regular_assigned_task?: boolean;
  is_user_template?: boolean;
  is_template?: boolean;
  metadata?: {
    type?: string;
    academic_data?: any[];
    faculty_score?: number;
    feedback_data?: any[];
    research_data?: any[];
    research_score?: number;
    teaching_data?: any[];
    teaching_score?: number;
    professional_data?: any[];
    professional_score?: number;
    research_project_data?: any[];
    research_project_score?: number;
    [key: string]: any;
  };
}

const TaskDetails: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const notificationService = useNotification();
  const { user: currentUser } = useAuth();
  const { isWorkMode } = useWorkingMode();
  const isPersonalMode = !isWorkMode;

  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [userRole, setUserRole] = useState<'admin' | 'team_lead' | 'member'>('member');

  const [task, setTask] = useState<TaskDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [hasFailedToLoad, setHasFailedToLoad] = useState(false);
  const isLoadingRef = useRef(false);
  const taskNotFoundRef = useRef(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    total: number;
  } | null>(null);

  // Load task details with proper loading state management
  const loadTaskDetails = useCallback(async () => {
    if (!taskId || isLoadingRef.current || taskNotFoundRef.current) {
      console.log('🚫 Skipping loadTaskDetails:', {
        taskId: !!taskId,
        isLoading: isLoadingRef.current,
        taskNotFound: taskNotFoundRef.current
      });
      return;
    }

    console.log('🔄 Loading task details for:', taskId);
    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      // Pass filter parameters based on mode: in personal mode, filter comments by current user
      const params = {
        filter_comments_by_user: isPersonalMode,
        work_mode: (isPersonalMode ? 'personal' : 'work') as 'personal' | 'work'
      };

      const response = await apiService.getTaskDetails(taskId, params);

      if (response.status === 'success') {
        console.log('✅ Task details loaded successfully');
        console.log('Task data:', response.data);
        console.log('is_instance:', response.data?.is_instance);
        console.log('allow_user_instances:', response.data?.allow_user_instances);
        setTask(response.data);
        setHasInitiallyLoaded(true);
        setHasFailedToLoad(false);
        setIsInitialLoad(false);
      } else {
        const errorMessage = response.message || 'Failed to load task details';
        setError(errorMessage);
        setHasFailedToLoad(true);

        // Check if task is not found (404 or similar error)
        if (response.status === 'error' && (
          errorMessage.toLowerCase().includes('not found') ||
          errorMessage.toLowerCase().includes('does not exist')
        )) {
          console.log('🚫 Task not found - setting flag to prevent further calls');
          taskNotFoundRef.current = true; // Prevent further API calls

          // Determine redirect path - prefer going back to template/group if available
          const redirectPath = groupId
            ? `/group-management/groups/${groupId}/task-management`
            : '/task-management';

          notificationService.error(errorMessage + ' Redirecting...');
          setTimeout(() => {
            navigate(redirectPath);
          }, 2000); // Redirect after 2 seconds
        } else {
          notificationService.error(errorMessage);
        }
      }
    } catch (err: any) {
      console.error('💥 Error loading task details:', err);
      setError(err);
      setHasFailedToLoad(true);
      setIsInitialLoad(false);

      // Check if it's a 404 error or task not found
      if (err.response?.status === 404 ||
        err.message?.toLowerCase().includes('not found') ||
        err.message?.toLowerCase().includes('does not exist')) {
        console.log('🚫 Task not found (catch) - setting flag to prevent further calls');
        taskNotFoundRef.current = true; // Prevent further API calls
        notificationService.error('Task not found. Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000); // Redirect after 2 seconds
      } else {
        notificationService.error(err.response?.data?.message || err.response?.data?.error || 'Failed to load task details');
      }
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [taskId, notificationService, navigate, isPersonalMode]);

  // Reset state when taskId changes
  useEffect(() => {
    if (taskId) {
      setHasInitiallyLoaded(false);
      setHasFailedToLoad(false);
      taskNotFoundRef.current = false;
      isLoadingRef.current = false;
      setTask(null);
      setError(null);
      setIsInitialLoad(true);
    }
  }, [taskId]);

  useEffect(() => {
    if (taskId && !hasInitiallyLoaded && !taskNotFoundRef.current) {
      loadTaskDetails();
    }
  }, [taskId, hasInitiallyLoaded, loadTaskDetails]); // Include loadTaskDetails in dependencies

  const refetchTaskDetails = useCallback(() => {
    if (!taskNotFoundRef.current) {
      loadTaskDetails();
    }
  }, [loadTaskDetails]);

  // Determine if user is admin based on role
  const isAdmin = currentUser?.role === 'admin';

  // Extract groupId from URL path (works for both admin and member contexts)
  const getGroupIdFromPath = () => {
    const pathSegments = location.pathname.split('/');
    // Check for /group-management/groups/:groupId/... pattern
    const groupManagementIndex = pathSegments.indexOf('groups');
    if (groupManagementIndex !== -1 && pathSegments[groupManagementIndex + 1]) {
      return pathSegments[groupManagementIndex + 1];
    }
    return null;
  };
  const groupId = getGroupIdFromPath();

  // Get active tab from URL path
  const getActiveTabFromUrl = () => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];

    // Check if last segment is a valid tab
    const validTabs = ['details', 'submissions', 'chat', 'updates', 'instances'];
    if (validTabs.includes(lastSegment)) {
      return lastSegment;
    }

    // Default to 'details' if no valid tab in URL
    return 'details';
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromUrl());

  // Handle tab change and update URL
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);

    // Update URL based on context
    const basePath = groupId
      ? `/group-management/groups/${groupId}/tasks/${taskId}`
      : `/task-management/${taskId}`;

    const newPath = tabId === 'details' ? basePath : `${basePath}/${tabId}`;
    navigate(newPath, { replace: true });
  };

  // Update active tab when URL changes
  useEffect(() => {
    const newActiveTab = getActiveTabFromUrl();
    if (newActiveTab !== activeTab) {
      setActiveTab(newActiveTab);
    }
  }, [location.pathname]);

  // Determine task type based on API response fields
  // Check if it is a template first
  const isTemplate = task ? (
    task.is_user_template === true ||
    task.allow_user_instances === true ||
    task.is_template === true
  ) : false;

  // If not a template, it is a regular task (including instances)
  const isRegularTask = task ? !isTemplate : false;

  // Debug logging to help troubleshoot
  if (task) {
    console.log('🔍 Task type detection:', {
      isRegularTask,
      isTemplate,
      is_user_instance: task.is_user_instance,
      is_regular_assigned_task: task.is_regular_assigned_task,
      is_user_template: task.is_user_template,
      allow_user_instances: task.allow_user_instances,
      taskName: task.name,
      metadata: task.metadata
    });
  }

  // Template-specific detection based on metadata type
  const isAcademicPerformance = task?.metadata?.type === 'academic_performance';
  const isFeedback = task?.metadata?.type === 'feedback';
  const isResearchPublication = task?.metadata?.type === 'research_publication';
  const isTeachingActivity = task?.metadata?.type === 'teaching_activity';
  const isProfessionalDevelopment = task?.metadata?.type === 'professional_development';
  const isResearchProject = task?.metadata?.type === 'research_project';

  // Tab configuration based on task type (template vs regular task)
  // Return empty array when task is not loaded to show skeleton loader
  const getAvailableTabs = () => {
    // If task is not loaded yet, return empty array to show skeleton loader
    if (!task) {
      return [];
    }

    // If this is a regular task (is_user_instance === false OR is_regular_assigned_task === true)
    // Show Task Details, Updates, and Submissions tabs
    if (isRegularTask) {
      // Regular tasks: Show Task Details, Updates, and Submissions
      return [
        {
          id: 'details',
          label: 'Task Details',
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14,2 14,8 20,8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10,9 9,9 8,9"></polyline>
            </svg>
          )
        },
        {
          id: 'updates',
          label: 'Task Updates',
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14,2 14,8 20,8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10,9 9,9 8,9"></polyline>
            </svg>
          )
        },
        {
          id: 'submissions',
          label: 'Submissions',
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14,2 14,8 20,8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
          )
        }
      ];
    }

    if (isTemplate) {
      // Template tasks: Show Template Details and Instances
      return [
        {
          id: 'details',
          label: 'Template Details',
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14,2 14,8 20,8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10,9 9,9 8,9"></polyline>
            </svg>
          )
        },
        {
          id: 'instances',
          label: 'Instances',
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
          )
        }
      ];
    } else {
      // Regular tasks: Show Task Details, Updates, and Submissions
      return [
        {
          id: 'details',
          label: 'Task Details',
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14,2 14,8 20,8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10,9 9,9 8,9"></polyline>
            </svg>
          )
        },
        {
          id: 'updates',
          label: 'Task Updates',
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14,2 14,8 20,8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10,9 9,9 8,9"></polyline>
            </svg>
          )
        },
        {
          id: 'submissions',
          label: 'Submissions',
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14,2 14,8 20,8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
          )
        }
      ];
    }
  };

  // Update tabs when task loads (to switch from default to template/regular tabs)
  useEffect(() => {
    if (task) {
      const availableTabs = getAvailableTabs().map(t => t.id);
      // If current tab is not valid for this task type, switch to details
      if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
        setActiveTab('details');
        const basePath = groupId
          ? `/group-management/groups/${groupId}/tasks/${taskId}`
          : `/task-management/${taskId}`;
        navigate(basePath, { replace: true });
      }
    }
  }, [task, isTemplate, isRegularTask]);

  // Determine user role based on current user's role
  useEffect(() => {
    if (currentUser) {
      if (currentUser.role === 'admin') {
        setUserRole('admin');
      } else if (currentUser.role === 'teamlead') {
        setUserRole('team_lead');
      } else {
        setUserRole('member');
      }
    }
  }, [currentUser]);


  const handleStatusUpdate = async (newStatus: string) => {
    if (!taskId) return;
    // For global tasks, status updates might need a different endpoint
    if (!task?.group?.id) {
      notificationService.error('Status updates for global tasks are not yet supported');
      return;
    }

    setUpdatingStatus(true);
    try {
      const response = await apiService.updateTaskStatus(taskId, task.group.id, {
        status: newStatus
      });

      if (response.status === 'success') {
        notificationService.success('Task status updated successfully');
        // Reload to get updated status
        refetchTaskDetails();
      } else {
        notificationService.error(response.message || 'Failed to update task status');
      }
    } catch (error: any) {
      console.error('Error updating task status:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to update task status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Status color helper - kept here as it's used in multiple places
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
      case 'not_started':
        return '#6b7280';
      case 'in_progress':
        return '#3b82f6';
      case 'completed':
        return '#10b981';
      case 'cancelled':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  // Dynamic timeline configuration based on backend rules or fallback to created/due date
  const getTimelineConfig = () => {
    if (!task) return { items: [], path: '', height: 0 };

    let timelineData: Array<{
      title: string;
      description: string;
      points: number;
      multiplier: number;
      date?: string;
      deadlineDate?: string;
      isPenalty?: boolean;
      isBonus?: boolean;
      isDeadline?: boolean;
      isCreated?: boolean;
      order: number;
    }> = [];

    // Check if we have deadline rules from backend
    if (task.deadline_rules && task.deadline_rules.length > 0) {
      const createdDate = new Date(task.created_at);
      const dueDate = task.due_date ? new Date(task.due_date) : null;

      // Create timeline items from rules
      const ruleItems = task.deadline_rules.map((rule: DeadlineRule) => {
        let points = 0;
        let description = rule.description || '';
        let order = 0;
        let isPenalty = false;
        let isBonus = false;
        let isDeadline = false;
        let isCreated = false;

        // Calculate points based on rule type
        switch (rule.calculation_type) {
          case 'fixed':
            points = rule.point_value;
            break;
          case 'percentage':
            points = Math.floor((task.base_points * rule.point_value) / 100);
            break;
          case 'multiplier':
            points = Math.floor(task.base_points * rule.point_value);
            break;
          default:
            points = task.base_points;
        }

        // Calculate specific deadline date for this milestone
        let deadlineDate: string | undefined;
        if (dueDate) {
          if (rule.time_period_type === 'before') {
            const days = rule.time_before?.days || 0;
            const hours = rule.time_before?.hours || 0;
            const minutes = rule.time_before?.minutes || 0;
            const seconds = rule.time_before?.seconds || 0;
            const milestoneDate = new Date(dueDate);
            milestoneDate.setDate(milestoneDate.getDate() - days);
            milestoneDate.setHours(milestoneDate.getHours() - hours);
            milestoneDate.setMinutes(milestoneDate.getMinutes() - minutes);
            milestoneDate.setSeconds(milestoneDate.getSeconds() - seconds);
            deadlineDate = milestoneDate.toISOString();
          } else if (rule.time_period_type === 'after') {
            const days = rule.time_after?.days || 0;
            const hours = rule.time_after?.hours || 0;
            const minutes = rule.time_after?.minutes || 0;
            const seconds = rule.time_after?.seconds || 0;
            const milestoneDate = new Date(dueDate);
            milestoneDate.setDate(milestoneDate.getDate() + days);
            milestoneDate.setHours(milestoneDate.getHours() + hours);
            milestoneDate.setMinutes(milestoneDate.getMinutes() + minutes);
            milestoneDate.setSeconds(milestoneDate.getSeconds() + seconds);
            deadlineDate = milestoneDate.toISOString();
          } else if (rule.time_period_type === 'exact') {
            deadlineDate = dueDate.toISOString();
          }
        }

        // Determine timeline position and properties
        if (rule.time_period_type === 'before') {
          isBonus = true;
          order = 1; // Before deadline
          const days = rule.time_before?.days || 0;
          const hours = rule.time_before?.hours || 0;
          const minutes = rule.time_before?.minutes || 0;
          const seconds = rule.time_before?.seconds || 0;
          description = `Early bonus deadline`;
        } else if (rule.time_period_type === 'after') {
          isPenalty = true;
          order = 3; // After deadline
          const days = rule.time_after?.days || 0;
          const hours = rule.time_after?.hours || 0;
          const minutes = rule.time_after?.minutes || 0;
          const seconds = rule.time_after?.seconds || 0;
          description = `Late penalty deadline`;
        } else if (rule.time_period_type === 'range') {
          order = 2; // Around deadline
          const fromDays = rule.time_range?.from?.days || 0;
          const toDays = rule.time_range?.to?.days || 0;
          const fromHours = rule.time_range?.from?.hours || 0;
          const toHours = rule.time_range?.to?.hours || 0;
          description = `Range deadline`;
        } else if (rule.time_period_type === 'exact') {
          isDeadline = true;
          order = 2; // At deadline
          description = 'Main deadline';
        }

        return {
          title: rule.name,
          description: description || `${rule.calculation_type} calculation`,
          points,
          multiplier: rule.point_value,
          deadlineDate,
          isPenalty,
          isBonus,
          isDeadline,
          isCreated,
          order
        };
      });

      // Add created date item
      timelineData.push({
        title: 'Task Created',
        description: `Task was created on ${createdDate.toLocaleDateString()}`,
        points: 0,
        multiplier: 0,
        isCreated: true,
        order: 0,
        date: createdDate.toISOString()
      });

      // Add deadline item if we have a due date
      if (dueDate) {
        timelineData.push({
          title: 'Deadline',
          description: `Main deadline`,
          points: task.base_points,
          multiplier: 1,
          deadlineDate: dueDate.toISOString(),
          isDeadline: true,
          order: 2,
          date: dueDate.toISOString()
        });
      }

      // Add rule items
      timelineData.push(...ruleItems);

      // Sort by deadline date (earliest first) - only items with deadlineDate
      timelineData.sort((a, b) => {
        // Items without deadlineDate (like "Task Created") should come first
        if (!a.deadlineDate && !b.deadlineDate) return a.order - b.order;
        if (!a.deadlineDate) return -1;
        if (!b.deadlineDate) return 1;

        // Sort by deadline date (earliest deadline first)
        return new Date(a.deadlineDate).getTime() - new Date(b.deadlineDate).getTime();
      });

    } else {
      // Fallback to created date and due date timeline
      const createdDate = new Date(task.created_at);
      const dueDate = task.due_date ? new Date(task.due_date) : null;

      timelineData = [
        {
          title: 'Task Created',
          description: `Task was created on ${createdDate.toLocaleDateString()}`,
          points: 0,
          multiplier: 0,
          isCreated: true,
          order: 0,
          date: createdDate.toISOString()
        }
      ];

      if (dueDate) {
        // Calculate specific deadlines for fallback timeline
        const earlyBonusDate = new Date(dueDate);
        earlyBonusDate.setDate(earlyBonusDate.getDate() - 5);

        const late1Date = new Date(dueDate);
        late1Date.setDate(late1Date.getDate() + 2);

        const late2Date = new Date(dueDate);
        late2Date.setDate(late2Date.getDate() + 5);

        const late3Date = new Date(dueDate);
        late3Date.setDate(late3Date.getDate() + 8);

        timelineData.push(
          {
            title: 'Early Bonus',
            description: `Early bonus deadline`,
            points: Math.floor(task.base_points * 1.2),
            multiplier: 1.2,
            deadlineDate: earlyBonusDate.toISOString(),
            isBonus: true,
            order: 1
          },
          {
            title: 'Main Deadline',
            description: `Main deadline`,
            points: task.base_points,
            multiplier: 1,
            deadlineDate: dueDate.toISOString(),
            isDeadline: true,
            order: 2,
            date: dueDate.toISOString()
          },
          {
            title: 'Late Penalty 1',
            description: `Late penalty deadline`,
            points: Math.floor(task.base_points * 0.9),
            multiplier: 0.9,
            deadlineDate: late1Date.toISOString(),
            isPenalty: true,
            order: 3
          },
          {
            title: 'Late Penalty 2',
            description: `Late penalty deadline`,
            points: Math.floor(task.base_points * 0.8),
            multiplier: 0.8,
            deadlineDate: late2Date.toISOString(),
            isPenalty: true,
            order: 4
          },
          {
            title: 'Late Penalty 3',
            description: `Late penalty deadline`,
            points: Math.floor(task.base_points * 0.6),
            multiplier: 0.6,
            deadlineDate: late3Date.toISOString(),
            isPenalty: true,
            order: 5
          }
        );

        // Sort by deadline date (earliest first) for fallback timeline
        timelineData.sort((a, b) => {
          // Items without deadlineDate (like "Task Created") should come first
          if (!a.deadlineDate && !b.deadlineDate) return a.order - b.order;
          if (!a.deadlineDate) return -1;
          if (!b.deadlineDate) return 1;

          // Sort by deadline date (earliest deadline first)
          return new Date(a.deadlineDate).getTime() - new Date(b.deadlineDate).getTime();
        });
      } else {
        timelineData.push({
          title: 'No Deadline',
          description: 'This task has no specific deadline',
          points: task.base_points,
          multiplier: 1,
          order: 1
        });
      }
    }

    const itemCount = timelineData.length;
    const containerWidth = 500;
    const centerX = containerWidth / 2;
    const startY = 80;
    const spacing = 160; // Space between cards
    const cardHeight = 180; // Card height

    // Calculate total height needed
    const totalHeight = startY + (itemCount * spacing) + 100;

    // Generate positions for each item
    const positions: Array<{ x: number; y: number; isRight: boolean }> = [];
    for (let i = 0; i < itemCount; i++) {
      const y = startY + (i * spacing);
      const x = i % 2 === 0 ? centerX + 100 : centerX - 100; // Alternate left-right with more spacing
      positions.push({ x, y, isRight: i % 2 === 0 });
    }

    // Generate SVG path
    let path = `M ${centerX} ${startY}`;
    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      const nextPos = positions[i + 1];

      if (nextPos) {
        const controlY1 = pos.y + (spacing * 0.3);
        const controlY2 = pos.y + (spacing * 0.7);
        path += ` C ${pos.x} ${controlY1} ${nextPos.x} ${controlY2} ${nextPos.x} ${nextPos.y}`;
      }
    }

    return {
      items: timelineData.map((item, index) => ({
        ...item,
        ...positions[index]
      })),
      path,
      height: totalHeight
    };
  };

  const timelineConfig = getTimelineConfig();

  // Countdown timer effect
  useEffect(() => {
    if (!task?.due_date) {
      setTimeRemaining(null);
      return;
    }

    const calculateTimeRemaining = () => {
      const now = new Date();
      const dueDate = new Date(task.due_date!);
      const difference = dueDate.getTime() - now.getTime();

      if (difference <= 0) {
        setTimeRemaining({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          total: 0
        });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining({
        days,
        hours,
        minutes,
        seconds,
        total: difference
      });
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [task?.due_date]);

  // Handle dynamic resizing and alignment
  useEffect(() => {
    const handleResize = () => {
      // Force re-render to recalculate positions
      const config = getTimelineConfig();
      // Update any dynamic calculations if needed
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [task]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No deadline set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDocumentUrl = (doc: any) => {
    if (!doc.file_url) {
      console.error('No file_url provided for document:', doc);
      return null;
    }

    // If URL is already absolute, return as is
    if (doc.file_url.startsWith('http')) {
      return doc.file_url;
    }

    // Convert relative URL to absolute URL
    // Handle both cases: URLs starting with /media/ and URLs without leading slash
    let relativeUrl = doc.file_url;
    if (!relativeUrl.startsWith('/')) {
      relativeUrl = '/' + relativeUrl;
    }

    const baseUrl = 'http://127.0.0.1:8000'; // Backend URL
    const absoluteUrl = `${baseUrl}${relativeUrl}`;

    console.log('URL conversion:', {
      original: doc.file_url,
      relative: relativeUrl,
      absolute: absoluteUrl
    });

    return absoluteUrl;
  };

  const handleViewDocument = (doc: any) => {
    try {
      console.log('Attempting to view document:', doc);
      const documentUrl = getDocumentUrl(doc);
      if (!documentUrl) {
        notificationService.error('Document URL not available');
        return;
      }

      console.log('Opening document URL:', documentUrl);

      // Check if it's a viewable file type
      const viewableTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'text/plain'];
      const lowerFileType = doc.file_type?.toLowerCase() || '';

      if (viewableTypes.some(type => lowerFileType.includes(type))) {
        // Open in new tab for viewable files
        const newWindow = window.open(documentUrl, '_blank');
        if (!newWindow) {
          notificationService.error('Popup blocked. Please allow popups for this site.');
        }
      } else {
        // For non-viewable files, trigger download
        handleDownloadDocument(doc);
      }
    } catch (error: any) {
      console.error('Error viewing document:', error);
      notificationService.error(error.message || 'Failed to view document');
    }
  };

  const handleDownloadDocument = (doc: any) => {
    try {
      console.log('Attempting to download document:', doc);
      const documentUrl = getDocumentUrl(doc);
      if (!documentUrl) {
        notificationService.error('Document URL not available');
        return;
      }

      console.log('Downloading document URL:', documentUrl);

      // Create a temporary link element to trigger download
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = doc.filename || doc.title;
      link.target = '_blank';

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notificationService.success(`Downloading ${doc.title}`);
    } catch (error: any) {
      console.error('Error downloading document:', error);
      notificationService.error(error.message || 'Failed to download document');
    }
  };

  // Individual Timeline Item Countdown Timer Component
  const TimelineItemCountdown = ({ deadlineDate, isMainDeadline = false }: { deadlineDate: string; isMainDeadline?: boolean }) => {
    const [timeRemaining, setTimeRemaining] = useState<{
      days: number;
      hours: number;
      minutes: number;
      seconds: number;
      total: number;
    } | null>(null);

    useEffect(() => {
      const calculateTimeRemaining = () => {
        const now = new Date();
        const deadline = new Date(deadlineDate);
        const difference = deadline.getTime() - now.getTime();

        if (difference <= 0) {
          setTimeRemaining({
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
            total: 0
          });
          return;
        }

        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeRemaining({
          days,
          hours,
          minutes,
          seconds,
          total: difference
        });
      };

      calculateTimeRemaining();
      const interval = setInterval(calculateTimeRemaining, 1000);
      return () => clearInterval(interval);
    }, [deadlineDate]);

    if (!timeRemaining) return null;

    const isOverdue = timeRemaining.total <= 0;
    const isUrgent = timeRemaining.days === 0 && timeRemaining.hours < 24;

    if (isMainDeadline) {
      return (
        <div className={`${styles.timelineItemCountdown} ${isOverdue ? styles.overdue : ''} ${isUrgent ? styles.urgent : ''}`}>
          <div className={styles.timelineCountdownDisplay}>
            <span className={styles.timelineTimeValue}>{Math.abs(timeRemaining.days)}d</span>
            <span className={styles.timelineTimeValue}>{Math.abs(timeRemaining.hours).toString().padStart(2, '0')}h</span>
            <span className={styles.timelineTimeValue}>{Math.abs(timeRemaining.minutes).toString().padStart(2, '0')}m</span>
            <span className={styles.timelineTimeValue}>{Math.abs(timeRemaining.seconds).toString().padStart(2, '0')}s</span>
          </div>
          <div className={styles.timelineCountdownLabel}>
            {isOverdue ? 'overdue' : 'remaining'}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.timelineItemCountdown}>
        <div className={`${styles.timelineCountdownDisplay} ${isOverdue ? styles.overdue : ''} ${isUrgent ? styles.urgent : ''}`}>
          <span className={styles.timelineTimeValue}>{Math.abs(timeRemaining.days)}d</span>
          <span className={styles.timelineTimeValue}>{Math.abs(timeRemaining.hours).toString().padStart(2, '0')}h</span>
          <span className={styles.timelineTimeValue}>{Math.abs(timeRemaining.minutes).toString().padStart(2, '0')}m</span>
          <span className={styles.timelineTimeValue}>{Math.abs(timeRemaining.seconds).toString().padStart(2, '0')}s</span>
        </div>
        <div className={styles.timelineCountdownLabel}>
          {isOverdue ? 'overdue' : 'remaining'}
        </div>
      </div>
    );
  };

  // Countdown Timer Component
  const CountdownTimer = ({ timeRemaining }: { timeRemaining: { days: number; hours: number; minutes: number; seconds: number; total: number } }) => {
    const isOverdue = timeRemaining.total <= 0;
    const isUrgent = timeRemaining.days === 0 && timeRemaining.hours < 24;

    // Don't show countdown if task is completed
    if (task?.status === 'completed') {
      return (
        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <div className={styles.countdownTimer}>

          </div>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <div className={styles.countdownTimer}>

          <div className={`${styles.countdownDisplay} ${isOverdue ? styles.overdue : ''} ${isUrgent ? styles.urgent : ''}`}>
            <div className={styles.timeUnit}>
              <div className={styles.timeValue}>{Math.abs(timeRemaining.days)}</div>
              <div className={styles.timeLabel}>Days</div>
            </div>
            <div className={styles.timeSeparator}>:</div>
            <div className={styles.timeUnit}>
              <div className={styles.timeValue}>{Math.abs(timeRemaining.hours).toString().padStart(2, '0')}</div>
              <div className={styles.timeLabel}>Hours</div>
            </div>
            <div className={styles.timeSeparator}>:</div>
            <div className={styles.timeUnit}>
              <div className={styles.timeValue}>{Math.abs(timeRemaining.minutes).toString().padStart(2, '0')}</div>
              <div className={styles.timeLabel}>Minutes</div>
            </div>
            <div className={styles.timeSeparator}>:</div>
            <div className={styles.timeUnit}>
              <div className={styles.timeValue}>{Math.abs(timeRemaining.seconds).toString().padStart(2, '0')}</div>
              <div className={styles.timeLabel}>Seconds</div>
            </div>
          </div>

          {isOverdue && (
            <div className={styles.overdueWarning}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              Task is overdue!
            </div>
          )}

          {isUrgent && !isOverdue && (
            <div className={styles.urgentWarning}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              Less than 24 hours remaining!
            </div>
          )}
        </div>

      </div>
    );
  };

  const getFileIcon = (fileType: string, filename?: string) => {
    const lowerFileType = fileType.toLowerCase();
    const lowerFilename = filename?.toLowerCase() || '';

    // Check for suspicious file extensions first
    const suspiciousExtensions = ['.php.pdf', '.exe.pdf', '.scr.pdf', '.bat.pdf', '.cmd.pdf'];
    const isSuspicious = suspiciousExtensions.some(ext => lowerFilename.includes(ext));

    if (isSuspicious) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" className={styles.suspiciousFileIcon}>
          <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8" />
          <path d="M9.653 5.496A3 3 0 0 0 8 5c-.61 0-1.179.183-1.653.496L4.694 2.992A5.97 5.97 0 0 1 8 2c1.222 0 2.358.365 3.306.992zm1.342 2.324a3 3 0 0 1-.884 2.312 3 3 0 0 1-.769.552l1.342 2.683c.57-.286 1.09-.66 1.538-1.103a6 6 0 0 0 1.767-4.624zm-5.679 5.548 1.342-2.684A3 3 0 0 1 5.005 7.82l-2.994-.18a6 6 0 0 0 3.306 5.728ZM10 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0" />
        </svg>
      );
    }

    if (lowerFileType.includes('pdf') || lowerFilename.endsWith('.pdf')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#ef4444" className={styles.fileIcon}>
          <path d="M5.523 10.424q.21-.124.459-.238a8 8 0 0 1-.45.606c-.28.337-.498.516-.635.572l-.035.012a.3.3 0 0 1-.026-.044c-.056-.11-.054-.216.04-.36.106-.165.319-.354.647-.548m2.455-1.647q-.178.037-.356.078a21 21 0 0 0 .5-1.05 12 12 0 0 0 .51.858q-.326.048-.654.114m2.525.939a4 4 0 0 1-.435-.41q.344.007.612.054c.317.057.466.147.518.209a.1.1 0 0 1 .026.064.44.44 0 0 1-.06.2.3.3 0 0 1-.094.124.1.1 0 0 1-.069.015c-.09-.003-.258-.066-.498-.256M8.278 4.97c-.04.244-.108.524-.2.829a5 5 0 0 1-.089-.346c-.076-.353-.087-.63-.046-.822.038-.177.11-.248.196-.283a.5.5 0 0 1 .145-.04c.013.03.028.092.032.198q.008.183-.038.465z" />
          <path d="M4 0h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2m.165 11.668c.09.18.23.343.438.419.207.075.412.04.58-.03.318-.13.635-.436.926-.786.333-.401.683-.927 1.021-1.51a11.6 11.6 0 0 1 1.997-.406c.3.383.61.713.91.95.28.22.603.403.934.417a.86.86 0 0 0 .51-.138c.155-.101.27-.247.354-.416.09-.181.145-.37.138-.563a.84.84 0 0 0-.2-.518c-.226-.27-.596-.4-.96-.465a5.8 5.8 0 0 0-1.335-.05 11 11 0 0 1-.98-1.686c.25-.66.437-1.284.52-1.794.036-.218.055-.426.048-.614a1.24 1.24 0 0 0-.127-.538.7.7 0 0 0-.477-.365c-.202-.043-.41 0-.601.077-.377.15-.576.47-.651.823-.073.34-.04.736.046 1.136.088.406.238.848.43 1.295a20 20 0 0 1-1.062 2.227 7.7 7.7 0 0 0-1.482.645c-.37.22-.699.48-.897.787-.21.326-.275.714-.08 1.103" />
        </svg>
      );
    } else if (lowerFileType.includes('excel') || lowerFileType.includes('spreadsheet') || lowerFilename.endsWith('.xlsx') || lowerFilename.endsWith('.xls')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#10b981" className={styles.fileIcon}>
          <path d="M6 12v-2h3v2z" />
          <path d="M9.293 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0M9.5 3.5v-2l3 3h-2a1 1 0 0 1-1-1M3 9h10v1h-3v2h3v1h-3v2H9v-2H6v2H5v-2H3v-1h2v-2H3z" />
        </svg>
      );
    } else if (lowerFileType.includes('word') || lowerFileType.includes('document') || lowerFilename.endsWith('.docx') || lowerFilename.endsWith('.doc')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#3b82f6" className={styles.fileIcon}>
          <path d="M12 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2M5.485 4.879l1.036 4.144.997-3.655a.5.5 0 0 1 .964 0l.997 3.655 1.036-4.144a.5.5 0 0 1 .97.242l-1.5 6a.5.5 0 0 1-.967.01L8 7.402l-1.018 3.73a.5.5 0 0 1-.967-.01l-1.5-6a.5.5 0 1 1 .97-.242z" />
        </svg>
      );
    } else if (lowerFileType.includes('image') || lowerFilename.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#8b5cf6" className={styles.fileIcon}>
          <path d="M8.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0" />
          <path d="M12 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2M3 2a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v8l-2.083-2.083a.5.5 0 0 0-.76.063L8 11 5.835 9.7a.5.5 0 0 0-.611.076L3 12z" />

        </svg>
      );
    } else if (lowerFileType.includes('zip') || lowerFileType.includes('archive') || lowerFilename.match(/\.(zip|rar|7z|tar|gz)$/)) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#f59e0b" className={styles.fileIcon}>
          <path d="M6.5 7.5a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v.938l.4 1.599a1 1 0 0 1-.416 1.074l-.93.62a1 1 0 0 1-1.109 0l-.93-.62a1 1 0 0 1-.415-1.074l.4-1.599zm2 0h-1v.938a1 1 0 0 1-.03.243l-.4 1.598.93.62.93-.62-.4-1.598a1 1 0 0 1-.03-.243z" />
          <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zm5.5-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9v1H8v1h1v1H8v1h1v1H7.5V5h-1V4h1V3h-1V2h1z" />
        </svg>
      );
    } else {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#6b7280" className={styles.fileIcon}>
          <path d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zm0 1h8a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1" />
        </svg>
      );
    }
  };

  // Tab content components
  const renderTabContent = () => {
    if (!task) return null;

    switch (activeTab) {
      case 'details': {
        // Find current user's assignment status
        const currentUserAssignment = task?.assignees?.find(
          (assignment: TaskAssignment) => assignment.user.id === currentUser?.id
        );

        return (
          <div className={styles.tabContent}>
            <TaskHeader
              task={task}
              timeRemaining={timeRemaining}
              isPersonalMode={isPersonalMode}
              isTemplate={isTemplate && !isRegularTask}
              currentUserId={currentUser?.id || null}
            />

            {/* Template-Specific Information Section */}
            {(isAcademicPerformance || isFeedback || isResearchPublication || isTeachingActivity || isProfessionalDevelopment || isResearchProject) && (
              <>
                {isAcademicPerformance && renderAcademicPerformanceDetails()}
                {isResearchPublication && renderResearchPublicationDetails()}
                {isTeachingActivity && renderTeachingActivityDetails()}
                {isResearchProject && renderResearchProjectDetails()}
                {isProfessionalDevelopment && renderProfessionalDevelopmentDetails()}
                {isFeedback && renderFeedbackDetails()}
              </>
            )}

            {/* In personal mode for tasks (not templates): show timeline, hide statistics */}
            {isPersonalMode && isRegularTask ? (
              <>
                {/* Point Timeline Section - Show for tasks in personal mode */}
                {timelineConfig.items.length > 0 && (
                  <div className={styles.pointTimelineSection}>
                    <div className={styles.timelineHeader}>
                      <h3 className={styles.sectionTitle}>Point Timeline</h3>
                    </div>
                    <div
                      className={styles.roadTimelineContainer}
                      style={{
                        height: `${timelineConfig.height}px`,
                        width: '500px',
                        margin: '0 auto'
                      }}
                    >
                      <svg
                        className={styles.roadTimelineSVG}
                        viewBox={`0 0 500 ${timelineConfig.height}`}
                        preserveAspectRatio="xMidYMid meet"
                      >
                        <defs>
                          <pattern id="dashPattern" patternUnits="userSpaceOnUse" width="8" height="2">
                            <rect width="6" height="2" fill="#5c3cfb" />
                          </pattern>
                        </defs>
                        <path
                          d={timelineConfig.path}
                          stroke="url(#dashPattern)"
                          strokeWidth="2"
                          fill="none"
                          className={styles.roadPath}
                        />
                      </svg>

                      {/* Dynamic Timeline Items */}
                      {timelineConfig.items.map((item, index) => (
                        <div
                          key={index}
                          className={styles.roadTimelineItem}
                          style={{
                            top: `${item.y + 80}px`,
                            left: index === 0 ? '0' : (item.isRight ? '100px' : '-100px'),
                            right: index === 0 ? '0' : 'auto'
                          }}
                        >
                          <div className={`${styles.roadConnectionCircle} ${item.isCreated ? styles.createdCircle :
                            item.isDeadline ? styles.deadlineCircle :
                              item.isBonus ? styles.bonusCircle :
                                item.isPenalty ? styles.penaltyCircle :
                                  styles.defaultCircle
                            }`}></div>
                          <div
                            className={`${styles.roadTimelineCard} ${item.isRight ? styles.roadCardRight : styles.roadCardLeft} ${item.isCreated ? styles.createdCard :
                              item.isDeadline ? styles.deadlineCard :
                                item.isBonus ? styles.bonusCard :
                                  item.isPenalty ? styles.penaltyCard :
                                    styles.defaultCard
                              }`}
                            style={{ marginLeft: index === 0 ? '2.5rem' : '1.5rem' }}
                          >
                            <div className={styles.roadTimelineTitle}>{item.title}</div>
                            <div className={styles.roadTimelineDescription}>{item.description}</div>

                            {/* Individual countdown timer for each milestone */}
                            {item.deadlineDate && (
                              <TimelineItemCountdown
                                deadlineDate={item.deadlineDate}
                                isMainDeadline={item.isDeadline}
                              />
                            )}

                            {/* Show static date only if no deadlineDate (for created items) */}
                            {item.date && !item.deadlineDate && (
                              <div className={styles.roadTimelineDate}>
                                {new Date(item.date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </div>
                            )}
                            <div className={`${styles.roadTimelinePoints} ${item.isPenalty ? styles.penaltyPoints :
                              item.isBonus ? styles.bonusPoints :
                                item.isDeadline ? styles.deadlinePoints :
                                  styles.defaultPoints
                              }`}>
                              {item.isPenalty ? (
                                <span className={styles.penaltySymbol}>-</span>
                              ) : item.isBonus ? (
                                <span className={styles.bonusSymbol}>+</span>
                              ) : null}
                              {item.points > 0 ? `${item.points} points` : 'No points'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Template Statistics Section (for templates) or Task Statistics Section (for regular tasks) */}
                {/* Hide statistics in personal mode for tasks, but ALWAYS show for templates */}
                {/* Hide statistics in personal mode for tasks, but ALWAYS show for templates. Also hide for instances. */}
                {(!isPersonalMode || (isTemplate && !isRegularTask)) && !(task.is_instance || task.is_user_instance) && (
                  <>
                    {isTemplate && !isRegularTask ? (
                      <TemplateStatistics
                        templateId={task.id}
                        groupId={task.group?.id || null}
                        taskId={task.id}
                      />
                    ) : (
                      <TaskStatistics
                        taskId={task.id}
                        groupId={task.group?.id || null}
                        assigneesCount={task.assignees.length}
                        inProgressCount={task.assignees.filter((a: TaskAssignment) => a.status === 'in_progress').length}
                        completedCount={task.assignees.filter((a: TaskAssignment) => a.status === 'completed').length}
                        totalPoints={task.base_points + task.team_lead_points}
                      />
                    )}

                    {/* Charts Section */}
                    <TaskCharts
                      taskId={task.id}
                      groupId={task.group?.id || null}
                      isTemplate={isTemplate && !isRegularTask}
                    />
                  </>
                )}

                {/* Point Timeline Section - Show for tasks (not templates) in work mode */}
                {isRegularTask && isWorkMode && timelineConfig.items.length > 0 && (
                  <div className={styles.pointTimelineSection}>
                    <div className={styles.timelineHeader}>
                      <h3 className={styles.sectionTitle}>Point Timeline</h3>

                    </div>
                    <div
                      className={styles.roadTimelineContainer}
                      style={{
                        height: `${timelineConfig.height}px`,
                        width: '500px',
                        margin: '0 auto'
                      }}
                    >
                      <svg
                        className={styles.roadTimelineSVG}
                        viewBox={`0 0 500 ${timelineConfig.height}`}
                        preserveAspectRatio="xMidYMid meet"
                      >
                        <defs>
                          <pattern id="dashPattern" patternUnits="userSpaceOnUse" width="8" height="2">
                            <rect width="6" height="2" fill="#5c3cfb" />
                          </pattern>
                        </defs>
                        <path
                          d={timelineConfig.path}
                          stroke="url(#dashPattern)"
                          strokeWidth="2"
                          fill="none"
                          className={styles.roadPath}
                        />
                      </svg>

                      {/* Dynamic Timeline Items */}
                      {timelineConfig.items.map((item, index) => (
                        <div
                          key={index}
                          className={styles.roadTimelineItem}
                          style={{
                            top: `${item.y + 80}px`,
                            left: index === 0 ? '0' : (item.isRight ? '100px' : '-100px'),
                            right: index === 0 ? '0' : 'auto'
                          }}
                        >
                          <div className={`${styles.roadConnectionCircle} ${item.isCreated ? styles.createdCircle :
                            item.isDeadline ? styles.deadlineCircle :
                              item.isBonus ? styles.bonusCircle :
                                item.isPenalty ? styles.penaltyCircle :
                                  styles.defaultCircle
                            }`}></div>
                          <div
                            className={`${styles.roadTimelineCard} ${item.isRight ? styles.roadCardRight : styles.roadCardLeft} ${item.isCreated ? styles.createdCard :
                              item.isDeadline ? styles.deadlineCard :
                                item.isBonus ? styles.bonusCard :
                                  item.isPenalty ? styles.penaltyCard :
                                    styles.defaultCard
                              }`}
                            style={{ marginLeft: index === 0 ? '2.5rem' : '1.5rem' }}
                          >
                            <div className={styles.roadTimelineTitle}>{item.title}</div>
                            <div className={styles.roadTimelineDescription}>{item.description}</div>

                            {/* Individual countdown timer for each milestone */}
                            {item.deadlineDate && (
                              <TimelineItemCountdown
                                deadlineDate={item.deadlineDate}
                                isMainDeadline={item.isDeadline}
                              />
                            )}

                            {/* Show static date only if no deadlineDate (for created items) */}
                            {item.date && !item.deadlineDate && (
                              <div className={styles.roadTimelineDate}>
                                {new Date(item.date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </div>
                            )}
                            <div className={`${styles.roadTimelinePoints} ${item.isPenalty ? styles.penaltyPoints :
                              item.isBonus ? styles.bonusPoints :
                                item.isDeadline ? styles.deadlinePoints :
                                  styles.defaultPoints
                              }`}>
                              {item.isPenalty ? (
                                <span className={styles.penaltySymbol}>-</span>
                              ) : item.isBonus ? (
                                <span className={styles.bonusSymbol}>+</span>
                              ) : null}
                              {item.points > 0 ? `${item.points} points` : 'No points'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      }

      case 'instances':
        // Show instances for template tasks
        return (
          <div className={styles.tabContent}>
            <TaskInstances
              templateId={task.id}
              groupId={task.group?.id || null}
            />
          </div>
        );

      case 'submissions':
        // Only show submissions for regular tasks (not templates)
        if (!isRegularTask) {
          return null;
        }
        return (
          <div className={styles.tabContent}>
            <TaskSubmission
              taskId={taskId!}
              groupId={task.group?.id || ''}
              userRole={userRole}
              isAdmin={userRole === 'admin' || userRole === 'team_lead'}
              onTaskUpdate={refetchTaskDetails}
              task={task ? {
                approver_ids: task.approver_ids || task.approvers?.map(approver => approver.user?.id || approver.id).filter(Boolean) || [],
                approvers: task.approvers || [],
                approval_type: task.approval_type || 'none',
                group: task.group || null,
                is_instance: task.is_instance || false
              } : undefined}
            />
          </div>
        );

      case 'chat':
        // Only show chat for regular tasks with groups
        if (!isRegularTask || !task.group) {
          return null;
        }
        return (
          <div className={styles.tabContent}>
            <div className={styles.chatSection}>
              <ChatContainer groupId={task.group.id} />
            </div>
          </div>
        );

      case 'updates': {
        // Only show updates for regular tasks (not templates)
        if (!isRegularTask) {
          return null;
        }
        // Find current user's assignment status
        const currentUserAssignment = task?.assignees?.find(
          (assignment: TaskAssignment) => assignment.user.id === currentUser?.id
        );

        return (
          <div className={styles.tabContent}>
            <div className={styles.updatesSection}>
              <TaskUpdates
                currentUserAssignment={currentUserAssignment}
                task={task}
                onTaskUpdate={refetchTaskDetails}
              />
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  // Template-specific render functions
  const renderAcademicPerformanceDetails = () => {
    if (!task?.metadata?.academic_data || task.metadata.academic_data.length === 0) return null;

    return (
      <div className={styles.templateSection}>
        <h3 className={styles.sectionTitle}>Academic Performance Details</h3>
        {task.metadata.faculty_score !== undefined && (
          <div className={styles.scoreDisplay}>
            <strong>Faculty Score:</strong> {task.metadata.faculty_score.toFixed(2)} points
          </div>
        )}
        <div className={styles.tableContainer}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Course Name</th>
                <th>Year/Sem</th>
                <th>No. of Students</th>
                <th>Lecture Completed (%)</th>
                <th>No. Passed</th>
                <th>No. First Classes</th>
                <th>No. Distinctions</th>
              </tr>
            </thead>
            <tbody>
              {task.metadata.academic_data.map((row: any, index: number) => (
                <tr key={index}>
                  <td>{row.sNo || index + 1}</td>
                  <td>{row.courseName || '-'}</td>
                  <td>{row.yearSem || '-'}</td>
                  <td>{row.noStudents || 0}</td>
                  <td>{row.lectureCompleted || 0}%</td>
                  <td>{row.noPassed || 0}</td>
                  <td>{row.noFirstClasses || 0}</td>
                  <td>{row.noDistinctions || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderResearchPublicationDetails = () => {
    if (!task?.metadata?.research_data || task.metadata.research_data.length === 0) return null;

    return (
      <div className={styles.templateSection}>
        <h3 className={styles.sectionTitle}>Research Publication Details</h3>
        {task.metadata.research_score !== undefined && (
          <div className={styles.scoreDisplay}>
            <strong>Research Score:</strong> {task.metadata.research_score.toFixed(2)} points
          </div>
        )}
        {task.metadata.research_data.map((pub: any, index: number) => (
          <div key={index} className={styles.publicationCard}>
            <h4>{pub.title || `Publication ${index + 1}`}</h4>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Journal Name:</span>
                <span className={styles.infoValue}>{pub.journalName || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>ISSN/ISBN:</span>
                <span className={styles.infoValue}>{pub.issnIsbn || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Indexing:</span>
                <span className={styles.infoValue}>{pub.indexing || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Impact Factor:</span>
                <span className={styles.infoValue}>{pub.impactFactor || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Co-Authors:</span>
                <span className={styles.infoValue}>{pub.coAuthors || 0}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Main Author:</span>
                <span className={styles.infoValue}>{pub.isMainAuthor ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTeachingActivityDetails = () => {
    if (!task?.metadata?.teaching_data || task.metadata.teaching_data.length === 0) return null;

    return (
      <div className={styles.templateSection}>
        <h3 className={styles.sectionTitle}>Teaching & Learning Activities</h3>
        {task.metadata.teaching_score !== undefined && (
          <div className={styles.scoreDisplay}>
            <strong>Teaching Score:</strong> {task.metadata.teaching_score.toFixed(2)} points
          </div>
        )}
        {task.metadata.teaching_data.map((activity: any, index: number) => (
          <div key={index} className={styles.activityCard}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.infoLabel}>Activity Description:</span>
                <span className={styles.infoValue}>{activity.activityDescription || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Quality Rating:</span>
                <span className={styles.infoValue}>
                  <span className={`${styles.badge} ${styles[`badge${activity.qualityRating}`]}`}>
                    {activity.qualityRating || '-'}
                  </span>
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderResearchProjectDetails = () => {
    if (!task?.metadata?.research_project_data || task.metadata.research_project_data.length === 0) return null;

    return (
      <div className={styles.templateSection}>
        <h3 className={styles.sectionTitle}>Research Projects & Guidance</h3>
        {task.metadata.research_project_score !== undefined && (
          <div className={styles.scoreDisplay}>
            <strong>Project Score:</strong> {task.metadata.research_project_score.toFixed(2)} points
          </div>
        )}
        {task.metadata.research_project_data.map((project: any, index: number) => (
          <div key={index} className={styles.projectCard}>
            <h4>{project.title || `Project ${index + 1}`}</h4>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Category:</span>
                <span className={styles.infoValue}>{project.category || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Details:</span>
                <span className={styles.infoValue}>{project.details || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Period/Year:</span>
                <span className={styles.infoValue}>{project.periodOrYear || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Amount/Count:</span>
                <span className={styles.infoValue}>{project.amountOrCount || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Status:</span>
                <span className={styles.infoValue}>
                  <span className={`${styles.badge} ${styles[`badge${project.status}`]}`}>
                    {project.status || '-'}
                  </span>
                </span>
              </div>
              {project.policyOrPatent !== undefined && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Policy/Patent:</span>
                  <span className={styles.infoValue}>{project.policyOrPatent ? 'Yes' : 'No'}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderProfessionalDevelopmentDetails = () => {
    if (!task?.metadata?.professional_data || task.metadata.professional_data.length === 0) return null;

    return (
      <div className={styles.templateSection}>
        <h3 className={styles.sectionTitle}>Professional Development Activities</h3>
        {task.metadata.professional_score !== undefined && (
          <div className={styles.scoreDisplay}>
            <strong>Development Score:</strong> {task.metadata.professional_score.toFixed(2)} points
          </div>
        )}
        {task.metadata.professional_data.map((activity: any, index: number) => (
          <div key={index} className={styles.developmentCard}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Activity Type:</span>
                <span className={styles.infoValue}>{activity.activityType || '-'}</span>
              </div>
              <div className={styles.infoItem} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.infoLabel}>Description:</span>
                <span className={styles.infoValue}>{activity.description || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Role/Duration:</span>
                <span className={styles.infoValue}>{activity.roleOrDuration || '-'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderFeedbackDetails = () => {
    if (!task?.metadata?.feedback_data || task.metadata.feedback_data.length === 0) return null;

    return (
      <div className={styles.templateSection}>
        <h3 className={styles.sectionTitle}>Feedback Details</h3>
        <div className={styles.tableContainer}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Course Name</th>
                <th>Year/Sem</th>
                <th>No. of Students</th>
                <th>Feedback Percentage</th>
              </tr>
            </thead>
            <tbody>
              {task.metadata.feedback_data.map((row: any, index: number) => (
                <tr key={index}>
                  <td>{row.sNo || index + 1}</td>
                  <td>{row.courseName || '-'}</td>
                  <td>{row.yearSem || '-'}</td>
                  <td>{row.noStudents || 0}</td>
                  <td>{row.feedbackPercentage || 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!task && !loading) {
    return (
      <div className={styles.taskDetailsContainer}>
        <div className={styles.errorState}>
          <h2>Task not found</h2>
          <p>The task you're looking for doesn't exist or you don't have permission to view it.</p>
          <button
            className={styles.backButton}
            onClick={() => navigate(groupId ? `/group-management/groups/${groupId}/task-management` : '/task-management')}
          >
            Back to Task Management
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.taskDetailsContainer}>
      <BreadcrumbNavigation
        groupId={groupId}
        groupName={task?.group?.name || null}
        activeTab={activeTab}
        isTemplate={isTemplate && !isRegularTask}
      />

      {/* Tab Container */}
      <div className={tabStyles.tabsContainer}>
        {getAvailableTabs().length === 0 ? (
          // Show skeleton loader when task is not loaded
          <div className={styles.tabSkeletonContainer}>
            <SkeletonLoader count={3} variant="custom" height="40px" width="120px" />
          </div>
        ) : (
          getAvailableTabs().map(tab => (
            <button
              key={tab.id}
              className={`${tabStyles.tab} ${activeTab === tab.id ? tabStyles.active : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span className={tabStyles.tabIcon}>{tab.icon}</span>
              {tab.label}
            </button>
          ))
        )}
      </div>

      {/* Tab Content */}
      <div className={tabStyles.tabContentContainer}>
        {isInitialLoad ? (
          <div className={styles.tabContent}>
            <SkeletonLoader count={5} height="40px" />
          </div>
        ) : error && (
          error.message?.toLowerCase().includes('not found') ||
          error.message?.toLowerCase().includes('does not exist') ||
          error.response?.status === 404
        ) ? (
          <div className={styles.tabContent}>
            <div className={styles.errorState}>
              <div className={styles.errorIcon}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </div>
              <h2>Task Not Found</h2>
              <p>
                The task you're looking for doesn't exist or you don't have permission to view it.
              </p>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-600)', marginTop: '1rem' }}>
                Redirecting to dashboard in a few seconds...
              </p>
              <button
                className={styles.errorButton}
                onClick={() => navigate('/dashboard')}
                style={{
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-base)',
                  fontWeight: '500',
                  cursor: 'pointer',
                  marginTop: '1rem'
                }}
              >
                Go to Dashboard Now
              </button>
            </div>
          </div>
        ) : (
          renderTabContent()
        )}
      </div>
    </div>
  );
};

export default TaskDetails;